import React, { useState, useEffect, useRef } from "react";
import { Table, Input, Button, Space, Popconfirm, message, Tooltip } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import axios from "axios";
import CategoryModal from "./Model/category";
import "./homeCategory.scss";

const BASE_URL = process.env.REACT_APP_CSC_API_BASE_URL;

const columnsBase = [
  {
    title: "#",
    dataIndex: "index",
    key: "index",
    width: 70,
    align: "center",
  },
  {
    title: "Display Name",
    dataIndex: "display_name",
    key: "display_name",
  },
  {
    title: "Slug",
    dataIndex: "slug",
    key: "slug",
  },
  {
    title: "Icon",
    dataIndex: "icon",
    key: "icon",
    render: (url) => url ? <img src={url} alt="icon" style={{ width: 32, height: 32 }} /> : null,
  },
  {
    title: "Biller Category",
    dataIndex: "biller_category",
    key: "biller_category",
  },
  {
    title: "Service Group",
    dataIndex: "service_group_name",
    key: "service_group_name",
  },
   {
    title: "Region Restrictions",
    dataIndex: "is_region_required",
    key: "is_region_required",
    render: (val) => val ? "Yes" : "No",
  },
];

const HomeCategory = () => {
  const [data, setData] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [serviceGroups, setServiceGroups] = useState([]);
  const searchRef = useRef();

  // Fetch categories
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}categories/csc-dashboard`);
      const payload = res.data.payload || [];
      setData(
        payload.map((item, idx) => ({
          ...item,
          index: idx + 1,
        }))
      );
      setTotalData(payload.length);
      setFilteredData(payload.map((item, idx) => ({ ...item, index: idx + 1 })));
    } catch (err) {
      setData([]);
      setFilteredData([]);
      setTotalData(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch service groups for dropdown
  const fetchServiceGroups = async () => {
    try {
      const res = await axios.get(`${BASE_URL}service-groups`);
      setServiceGroups(res.data.payload || []);
    } catch {
      setServiceGroups([]);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchServiceGroups();
  }, []);

  // Debounced search (client-side)
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (searchRef.current) clearTimeout(searchRef.current);
    searchRef.current = setTimeout(() => {
      if (!value) {
        setFilteredData(data);
      } else {
        setFilteredData(
          data.filter(
            (item) =>
              item.display_name?.toLowerCase().includes(value.toLowerCase()) ||
              item.slug?.toLowerCase().includes(value.toLowerCase()) ||
              item.biller_category?.toLowerCase().includes(value.toLowerCase()) ||
              item.service_group_name?.toLowerCase().includes(value.toLowerCase())
          )
        );
      }
    }, 400);
  };

  // Add/Edit Modal
  const openAddModal = () => {
    setEditData(null);
    setModalOpen(true);
  };
  const openEditModal = (row) => {
    setEditData(row);
    setModalOpen(true);
  };
  const closeModal = (refresh = false) => {
    setModalOpen(false);
    setEditData(null);
    if (refresh) fetchCategories();
  };

  // Delete
  const handleDelete = async (row) => {
    try {
      await axios.delete(`${BASE_URL}categories/csc-dashboard/${row.service_id}`);
      message.success("Category deleted!");
      fetchCategories();
    } catch {
      message.error("Failed to delete category");
    }
  };

  // Columns with actions
  const columns = [
    ...columnsBase,
    {
      title: "Actions",
      key: "actions",
      render: (_, row) => (
        <Space>
          <Tooltip title="Edit">
            <Button icon={<EditOutlined />} onClick={() => openEditModal(row)} />
          </Tooltip>
          <Popconfirm
            title="Are you sure to delete this category?"
            onConfirm={() => handleDelete(row)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="mainContain">
      <h1 className="transaction">All Categories</h1>
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between" }}>
        <Input.Search
          placeholder="Search Category"
          allowClear
          value={searchInput}
          onChange={handleSearch}
          style={{ width: 300 }}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
          Add Category
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        rowKey="service_id"
        pagination={false}
        scroll={{ x: 900 }}
      />
      <CategoryModal
        open={modalOpen}
        onClose={closeModal}
        editData={editData}
        serviceGroups={serviceGroups}
      />
    </div>
  );
};

export default HomeCategory;